import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';

import Spinner from 'Shared/components/Spinner';
import { useWaitForCalendarWorkspacesSynced } from 'Shared/utils/hooks';
import { getWorkspacesWithoutRecurringMeeting } from 'Shared/selectors/workspace';

import SyncMeetingsModalBody from './SyncMeetingsModalBody/SyncMeetingsModalBody';
import SyncMeetingsModalBodyFinalStep from './SyncMeetingsModalBodyFinalStep/SyncMeetingsModalBodyFinalStep';
import SyncMeetingsModalButtonsArea from './SyncMeetingsModalButtonsArea/SyncMeetingsModalButtonsArea';
import { SyncMeetingsModalButtons } from './types';
import './SyncMeetingsModal.scss';

type Props = {
  onToggle: () => void;
  workspaceId?: string;
};

const finalStepId = 'final-step';

function SyncMeetingsModal({ onToggle, workspaceId }: Props) {
  const workspaces = useSelector(getWorkspacesWithoutRecurringMeeting(workspaceId));

  const [levelIndexForSync, setLevelIndexForSync] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const [startFinishSubmit, setStartFinishSubmit] = useState(false);
  const isLoading = useWaitForCalendarWorkspacesSynced({ syncStatusRequestCondition: !workspaceId });

  const countWorkspaces = workspaces.length;
  const levelsForSync = [...workspaces.map(({ id }) => id), finalStepId];
  const countLevelsForSync = levelsForSync.length;

  const handleCancelClick = useCallback(() => {
    (window as any).analytics.track('Clicked in Calendar Onboarding Modal', {
      button: 'cancel',
    });

    onToggle();
  }, [onToggle]);

  const handleUpdateFinishSubmit = useCallback(status => setStartFinishSubmit(status), []);

  const handleNextLevel = () => {
    const nextIndex = levelIndexForSync + 1;
    if (nextIndex <= countLevelsForSync - 1 && !workspaceId) {
      (window as any).analytics.track('Clicked in Calendar Onboarding Modal', {
        countWorkspaces,
        levelIndexForSync,
        button: 'skip',
      });

      return setLevelIndexForSync(nextIndex);
    }

    return onToggle();
  };

  const handleFinishClick = useCallback(() => setStartFinishSubmit(true), []);

  // Hack to make it impossible to get the index out of bounds error
  const displayFinalStep = levelIndexForSync >= countWorkspaces;

  return (
    <WpModal isOpen onToggle={onToggle} size="sm" zIndex={1100}>
      <WpModalHeader>{workspaceId ? 'Choose meeting to sync' : 'SYNC CALENDAR MEETINGS'}</WpModalHeader>
      <WpModalBody>
        {isLoading && <Spinner activeIf />}
        {!isLoading && !displayFinalStep && (
          <SyncMeetingsModalBody
            workspace={workspaces[levelIndexForSync]}
            onNextLevel={handleNextLevel}
            countWorkspaces={countLevelsForSync}
            currentWorkspaceIndex={levelIndexForSync + 1}
            showCountWorkspaces={Boolean(!workspaceId)}
            showResyncButton={Boolean(workspaceId)}
          />
        )}
        {!isLoading && displayFinalStep && (
          <SyncMeetingsModalBodyFinalStep
            setDisabledSubmit={setDisabledSubmit}
            countWorkspaces={countLevelsForSync}
            startSubmit={startFinishSubmit}
            onUpdateStartSubmit={handleUpdateFinishSubmit}
            setIsSubmitting={setIsSubmitting}
            onToggle={onToggle}
          />
        )}
      </WpModalBody>
      {!isLoading && !workspaceId && (
        <WpModalFooter>
          <SyncMeetingsModalButtonsArea
            onCancleClick={handleCancelClick}
            isSubmitting={isSubmitting}
            disabledSubmit={disabledSubmit}
            onPrimaryClick={displayFinalStep ? handleFinishClick : handleNextLevel}
            primaryButtonType={displayFinalStep ? SyncMeetingsModalButtons.FINISH : SyncMeetingsModalButtons.SKIP}
          />
        </WpModalFooter>
      )}
    </WpModal>
  );
}

export default SyncMeetingsModal;
